// src/components/operations/JobsTab.jsx
// Jobs tab with server-side pagination
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGraphQL } from '../../graphql';
import { usePagination } from '../../hooks/usePagination';
import { WORKFLOWS, getWorkflowByActivity, jobTypeToLabel } from '../jobs/workflows/WorkflowDefinitions';
import { JobEventTimeline } from '../jobs/timeline';
import { formatRelativeDate, formatDuration } from '../../utils/formatters';
import { JOB_STATUS_STYLES_BRUTALIST } from '../../constants/colors';
import {
    ArrowPathIcon,
    PlayIcon,
    StopIcon,
    ClockIcon,
    ChevronRightIcon,
    ChevronLeftIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const JobsTab = ({
    selectedJobId,
    onSelectJob,
    autoRefresh,
    refreshTrigger,
}) => {
    const { executeQuery, executeMutation, queries, mutations, loading } = useGraphQL();

    // State
    const [jobs, setJobs] = useState([]);
    const [totalJobs, setTotalJobs] = useState(0);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [selectedJobIds, setSelectedJobIds] = useState(new Set());
    const [actionFeedback, setActionFeedback] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null);

    // Stale job detection
    const [staleJobs, setStaleJobs] = useState([]);
    const [staleThreshold] = useState(2);
    const [checkingStale, setCheckingStale] = useState(false);

    // Pagination
    const pagination = usePagination({
        initialPage: 1,
        initialPageSize: PAGE_SIZE,
        totalItems: totalJobs,
    });

    // Load jobs with pagination
    const loadJobs = useCallback(async () => {
        try {
            const data = await executeQuery(queries.getJobs(), {
                limit: PAGE_SIZE,
                offset: pagination.offset,
                status: statusFilter || undefined,
                isScheduled: false,
            });
            const loadedJobs = data?.adminJobs || [];
            setJobs(loadedJobs);

            // Since API doesn't return totalCount, we estimate based on returned items
            // If we got a full page, there might be more
            if (loadedJobs.length === PAGE_SIZE) {
                setTotalJobs(Math.max(totalJobs, pagination.offset + PAGE_SIZE + 1));
            } else {
                setTotalJobs(pagination.offset + loadedJobs.length);
            }
            pagination.setTotalItems(pagination.offset + loadedJobs.length + (loadedJobs.length === PAGE_SIZE ? PAGE_SIZE : 0));
        } catch (err) {
            console.error('Failed to load jobs:', err);
        }
    }, [pagination.offset, statusFilter, executeQuery, queries]);

    // Load jobs on mount and when pagination/filters change
    useEffect(() => {
        loadJobs();
    }, [pagination.currentPage, statusFilter, refreshTrigger]);

    // Auto-refresh
    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(loadJobs, 5000);
        return () => clearInterval(interval);
    }, [autoRefresh, loadJobs]);

    // Show partial row data from the list immediately when a row is selected
    // (keeps UI responsive while the full job fetches in the background).
    useEffect(() => {
        if (!selectedJobId) return;
        const jobInPage = jobs.find(j => j.id === selectedJobId);
        if (!jobInPage) return;
        setSelectedJob(prev => (prev?.id === selectedJobId ? prev : jobInPage));
    }, [selectedJobId, jobs]);

    // Auto-select the matching workflow only when the selection changes —
    // not on every auto-refresh, which would override a manual left-panel pick.
    useEffect(() => {
        if (!selectedJobId) return;
        const jobInPage = jobs.find(j => j.id === selectedJobId);
        if (!jobInPage) return;
        const workflow = getWorkflowByActivity(jobInPage.type);
        if (workflow) setSelectedWorkflow(workflow.key);
    }, [selectedJobId]);

    // Fetch the full job (including `result`, which is omitted from the list
    // query to keep payloads small) whenever the selection changes.
    useEffect(() => {
        if (!selectedJobId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await executeQuery(queries.getJob(), { id: selectedJobId });
                if (cancelled || !data?.job) return;
                setSelectedJob(data.job);
                const workflow = getWorkflowByActivity(data.job.type);
                if (workflow) setSelectedWorkflow(workflow.key);
            } catch (err) {
                if (!cancelled) console.error('Failed to fetch job:', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedJobId, executeQuery, queries]);

    // Clear feedback after 5 seconds
    useEffect(() => {
        if (!actionFeedback) return;
        const timer = setTimeout(() => setActionFeedback(null), 5000);
        return () => clearTimeout(timer);
    }, [actionFeedback]);

    // Compute workflow stats
    const workflowStats = useMemo(() => {
        const stats = {};
        Object.keys(WORKFLOWS).forEach(key => {
            stats[key] = { total: 0, running: 0, pending: 0, failed: 0 };
        });
        jobs.forEach(job => {
            const workflow = getWorkflowByActivity(job.type);
            if (workflow) {
                stats[workflow.key].total++;
                if (job.status === 'RUNNING') stats[workflow.key].running++;
                if (job.status === 'PENDING') stats[workflow.key].pending++;
                if (job.status === 'FAILED') stats[workflow.key].failed++;
            }
        });
        return stats;
    }, [jobs]);

    // Filter jobs by workflow
    const filteredJobs = useMemo(() => {
        if (!selectedWorkflow) return jobs;
        const workflow = WORKFLOWS[selectedWorkflow];
        if (!workflow) return jobs;
        return jobs.filter(job => workflow.activities.includes(job.type));
    }, [jobs, selectedWorkflow]);

    // Stale job helpers
    const isJobStale = (job, thresholdMinutes = 2) => {
        if (job.status !== 'RUNNING') return false;
        if (!job.heartbeatAt) return true;
        const heartbeatAge = (Date.now() - new Date(job.heartbeatAt).getTime()) / 1000 / 60;
        return heartbeatAge > thresholdMinutes;
    };

    // Job actions
    const showFeedback = (type, message) => setActionFeedback({ type, message });

    const executeJobAction = async (mutation, variables, responseKey, actionLabel) => {
        try {
            const result = await executeMutation(mutation, variables);
            const response = result?.[responseKey];
            if (response && response.success === false) {
                showFeedback('error', `${actionLabel} failed: ${response.error || 'Unknown error'}`);
                return false;
            }
            loadJobs();
            return true;
        } catch (err) {
            showFeedback('error', `${actionLabel} failed: ${err.message}`);
            return false;
        }
    };

    const startJob = (jobId) => executeJobAction(mutations.adminStartJob, { jobId }, 'adminStartJob', 'Start job');
    const retryJob = (jobId) => executeJobAction(mutations.adminRetryJob, { jobId }, 'adminRetryJob', 'Retry job');
    const cancelJob = (jobId) => executeJobAction(mutations.adminCancelJob, { jobId }, 'adminCancelJob', 'Cancel job');

    // Check stale jobs
    const checkStaleJobs = async () => {
        setCheckingStale(true);
        try {
            const data = await executeQuery(queries.getStaleJobs(), { thresholdMinutes: staleThreshold });
            if (data?.adminGetStaleJobs?.success) {
                setStaleJobs(data.adminGetStaleJobs.jobs || []);
                if (data.adminGetStaleJobs.count === 0) {
                    showFeedback('success', 'No stale jobs found');
                }
            }
        } catch (err) {
            showFeedback('error', `Failed to check stale jobs: ${err.message}`);
        } finally {
            setCheckingStale(false);
        }
    };

    const handleResetStaleJobs = async () => {
        if (!confirm(`Reset ${staleJobs.length} stale job(s) back to PENDING status?`)) return;
        try {
            const result = await executeMutation(mutations.adminResetStaleJobs, { thresholdMinutes: staleThreshold });
            if (result?.adminResetStaleJobs?.success) {
                showFeedback('success', `Reset ${result.adminResetStaleJobs.resetCount} stale job(s) to pending`);
                setStaleJobs([]);
                loadJobs();
            }
        } catch (err) {
            showFeedback('error', `Failed to reset stale jobs: ${err.message}`);
        }
    };

    // Selection helpers
    const toggleJobSelection = (jobId) => {
        setSelectedJobIds(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const visibleIds = filteredJobs.map(j => j.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedJobIds.has(id));
        if (allSelected) {
            setSelectedJobIds(new Set());
        } else {
            setSelectedJobIds(new Set(visibleIds));
        }
    };

    // Bulk actions
    const executeBulkAction = async (mutationKey, statusList, actionLabel) => {
        const targetJobs = filteredJobs.filter(
            j => selectedJobIds.has(j.id) && statusList.includes(j.status)
        );
        if (targetJobs.length === 0) return;

        setBulkProgress({ current: 0, total: targetJobs.length, action: actionLabel });
        let succeeded = 0;
        let failed = 0;

        for (let i = 0; i < targetJobs.length; i++) {
            setBulkProgress({ current: i + 1, total: targetJobs.length, action: actionLabel });
            try {
                const result = await executeMutation(mutations[mutationKey], { jobId: targetJobs[i].id });
                if (result?.[mutationKey]?.success === false) {
                    failed++;
                } else {
                    succeeded++;
                }
            } catch {
                failed++;
            }
        }

        setBulkProgress(null);
        setSelectedJobIds(new Set());
        loadJobs();

        if (failed === 0) {
            showFeedback('success', `${actionLabel}: ${succeeded} job${succeeded !== 1 ? 's' : ''} processed`);
        } else {
            showFeedback('error', `${actionLabel}: ${succeeded} succeeded, ${failed} failed`);
        }
    };

    // Selected stats for bulk actions
    const selectedStats = useMemo(() => {
        const selected = filteredJobs.filter(j => selectedJobIds.has(j.id));
        return {
            total: selected.length,
            pendingCount: selected.filter(j => j.status === 'PENDING').length,
            cancelableCount: selected.filter(j => j.status === 'PENDING' || j.status === 'RUNNING').length,
            retryableCount: selected.filter(j => j.status === 'FAILED' || j.status === 'COMPLETED').length,
        };
    }, [filteredJobs, selectedJobIds]);

    // Reset pagination when filter changes
    useEffect(() => {
        pagination.reset();
        setSelectedJobIds(new Set());
    }, [selectedWorkflow, statusFilter]);

    return (
        <div className="h-full flex">
            {/* Left Panel - Workflow List */}
            <div className="w-64 border-r border-gray-800 bg-gray-900 overflow-y-auto">
                <div className="p-2">
                    <button
                        onClick={() => setSelectedWorkflow(null)}
                        className={`w-full text-left px-3 py-2 mb-1 rounded-lg font-mono text-sm transition-all duration-150 ${
                            selectedWorkflow === null
                                ? 'bg-primary/10 text-primary-dark border-l-2 border-primary'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'
                        }`}
                    >
                        <span className="block font-bold">ALL RUNS</span>
                        <span className="text-xs text-slate-500">{jobs.length} loaded</span>
                    </button>

                    <div className="border-t border-slate-200 my-2" />

                    {Object.entries(WORKFLOWS).map(([key, workflow]) => {
                        const stats = workflowStats[key];
                        const isSelected = selectedWorkflow === key;
                        const Icon = workflow.icon;

                        return (
                            <button
                                key={key}
                                onClick={() => setSelectedWorkflow(key)}
                                className={`w-full text-left px-3 py-2 mb-1 rounded-lg font-mono text-sm transition-all duration-150 ${
                                    isSelected
                                        ? 'bg-primary/10 text-primary-dark border-l-2 border-primary'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Icon className={`h-4 w-4 ${workflow.textColor}`} />
                                        <span className="font-bold uppercase text-xs">{workflow.label}</span>
                                    </div>
                                    {stats?.total > 0 && (
                                        <span className="text-xs text-slate-500">{stats.total}</span>
                                    )}
                                </div>
                                {(stats?.running > 0 || stats?.pending > 0 || stats?.failed > 0) && (
                                    <div className="flex items-center gap-2 mt-1 text-xs">
                                        {stats.running > 0 && <span className="text-blue-600">{stats.running}R</span>}
                                        {stats.pending > 0 && <span className="text-yellow-600">{stats.pending}P</span>}
                                        {stats.failed > 0 && <span className="text-red-600">{stats.failed}F</span>}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Center Panel - Jobs Table */}
            <div className="flex-1 flex flex-col min-w-0 bg-gray-950">
                {/* Table Header */}
                <div className="border-b border-gray-800 bg-gray-900 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="font-mono text-sm text-slate-600">
                            {selectedWorkflow ? WORKFLOWS[selectedWorkflow]?.label : 'All Workflows'}
                        </span>
                        <span className="font-mono text-xs text-slate-400">
                            {filteredJobs.length} runs
                        </span>
                        <button
                            onClick={checkStaleJobs}
                            disabled={checkingStale}
                            className={`px-2 py-1 rounded-lg text-xs font-mono border transition-all duration-150 ${
                                staleJobs.length > 0
                                    ? 'bg-orange-100 text-orange-700 border-orange-300'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                            } disabled:opacity-50`}
                            title="Check for stale jobs"
                        >
                            <ExclamationTriangleIcon className={`h-3 w-3 inline mr-1 ${checkingStale ? 'animate-pulse' : ''}`} />
                            STALE
                            {staleJobs.length > 0 && (
                                <span className="ml-1 px-1 rounded bg-orange-600 text-on-accent text-xs font-bold">
                                    {staleJobs.length}
                                </span>
                            )}
                        </button>
                    </div>
                    <div className="flex items-center gap-1">
                        {[null, 'PENDING', 'RUNNING', 'FAILED', 'COMPLETED'].map(status => (
                            <button
                                key={status || 'all'}
                                onClick={() => setStatusFilter(status)}
                                className={`px-2 py-1 rounded-lg text-xs font-mono font-bold transition-all duration-150 ${
                                    statusFilter === status
                                        ? 'bg-primary text-on-accent'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                {status || 'ALL'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Feedback Banner */}
                {actionFeedback && (
                    <div className={`px-4 py-2 font-mono text-xs font-bold flex items-center justify-between ${
                        actionFeedback.type === 'error'
                            ? 'bg-red-50 text-red-700 border-b border-red-200'
                            : 'bg-green-50 text-green-700 border-b border-green-200'
                    }`}>
                        <span>{actionFeedback.message}</span>
                        <button onClick={() => setActionFeedback(null)} className="hover:opacity-70">
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {/* Stale Jobs Warning */}
                {staleJobs.length > 0 && (
                    <div className="bg-orange-50 border-b border-orange-200 px-4 py-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                                <span className="font-mono text-sm font-bold text-orange-700">
                                    {staleJobs.length} STALE JOB{staleJobs.length !== 1 ? 'S' : ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleResetStaleJobs}
                                    className="px-2 py-1 rounded-lg bg-orange-600 text-on-accent font-mono text-xs font-bold hover:bg-orange-500 transition-all duration-150"
                                >
                                    RESET ALL
                                </button>
                                <button onClick={() => setStaleJobs([])} className="text-orange-500 hover:text-orange-700">
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Progress */}
                {bulkProgress && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 font-mono text-xs text-blue-700 flex items-center gap-3">
                        <span className="font-bold">{bulkProgress.action}</span>
                        <span>{bulkProgress.current} / {bulkProgress.total}</span>
                        <div className="flex-1 h-1 rounded bg-blue-100">
                            <div
                                className="h-full rounded bg-blue-500 transition-all"
                                style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Bulk Actions Bar */}
                {selectedStats.total > 0 && !bulkProgress && (
                    <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-3 font-mono text-xs">
                        <span className="text-slate-900 font-bold">{selectedStats.total} SELECTED</span>
                        <div className="flex items-center gap-2">
                            {selectedStats.pendingCount > 0 && (
                                <button
                                    onClick={() => executeBulkAction('adminStartJob', ['PENDING'], 'START')}
                                    className="px-3 py-1 rounded-lg bg-green-600 text-on-accent font-bold hover:bg-green-500 transition-all duration-150"
                                >
                                    START {selectedStats.pendingCount}
                                </button>
                            )}
                            {selectedStats.cancelableCount > 0 && (
                                <button
                                    onClick={() => executeBulkAction('adminCancelJob', ['PENDING', 'RUNNING'], 'CANCEL')}
                                    className="px-3 py-1 rounded-lg bg-red-600 text-on-accent font-bold hover:bg-red-500 transition-all duration-150"
                                >
                                    CANCEL {selectedStats.cancelableCount}
                                </button>
                            )}
                            {selectedStats.retryableCount > 0 && (
                                <button
                                    onClick={() => executeBulkAction('adminRetryJob', ['FAILED', 'COMPLETED'], 'RETRY')}
                                    className="px-3 py-1 rounded-lg bg-blue-600 text-on-accent font-bold hover:bg-blue-500 transition-all duration-150"
                                >
                                    RETRY {selectedStats.retryableCount}
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => setSelectedJobIds(new Set())}
                            className="ml-auto text-slate-500 hover:text-slate-900"
                        >
                            CLEAR
                        </button>
                    </div>
                )}

                {/* Jobs Table */}
                <div className="flex-1 overflow-auto">
                    <table className="w-full">
                        <thead className="sticky top-0 bg-gray-900 border-b border-gray-800">
                            <tr className="font-mono text-xs text-gray-500 uppercase">
                                <th className="px-2 py-2 w-8">
                                    <input
                                        type="checkbox"
                                        checked={filteredJobs.length > 0 && filteredJobs.every(j => selectedJobIds.has(j.id))}
                                        onChange={toggleSelectAll}
                                        className="accent-primary"
                                    />
                                </th>
                                <th className="text-left px-4 py-2 font-medium">Run ID</th>
                                <th className="text-left px-4 py-2 font-medium">Type</th>
                                <th className="text-left px-4 py-2 font-medium">Status</th>
                                <th className="text-left px-4 py-2 font-medium">Started</th>
                                <th className="text-left px-4 py-2 font-medium">Duration</th>
                                <th className="text-left px-4 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-900">
                            {filteredJobs.map(job => {
                                const workflow = getWorkflowByActivity(job.type);
                                const label = jobTypeToLabel(job.type);
                                const stale = isJobStale(job, staleThreshold);

                                return (
                                    <tr
                                        key={job.id}
                                        onClick={() => {
                                            setSelectedJob(job);
                                            onSelectJob?.(job.id);
                                        }}
                                        className={`cursor-pointer transition-all duration-150 ${
                                            selectedJob?.id === job.id ? 'bg-primary/10' : 'hover:bg-slate-50'
                                        }`}
                                    >
                                        <td className="px-2 py-2 w-8">
                                            <input
                                                type="checkbox"
                                                checked={selectedJobIds.has(job.id)}
                                                onChange={(e) => { e.stopPropagation(); toggleJobSelection(job.id); }}
                                                onClick={(e) => e.stopPropagation()}
                                                className="accent-primary"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-mono text-xs text-gray-300">{job.id.substring(0, 8)}</div>
                                            {job.tenderId && (
                                                <a
                                                    href={`https://www.bidlyzer.com/tender/${job.tenderId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="font-mono text-xs text-gray-600 hover:text-blue-700"
                                                >
                                                    {job.tenderId.substring(0, 8)}
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                {workflow && <workflow.icon className={`h-4 w-4 ${workflow.textColor}`} />}
                                                <div>
                                                    <div className="font-mono text-xs text-slate-900">{label || job.type}</div>
                                                    {job.isScheduled && (
                                                        <div className="font-mono text-xs text-blue-700 flex items-center gap-1">
                                                            <ClockIcon className="h-3 w-3" />
                                                            {job.enabled ? 'scheduled' : 'paused'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-1">
                                                <span className={`px-2 py-0.5 font-mono text-xs font-bold ${JOB_STATUS_STYLES_BRUTALIST[job.status]}`}>
                                                    {job.status}
                                                </span>
                                                {stale && (
                                                    <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-mono text-xs font-bold animate-pulse">
                                                        STALE
                                                    </span>
                                                )}
                                            </div>
                                            {job.progressPercent != null && job.status === 'RUNNING' && (
                                                <div className="mt-1 w-16 h-1 rounded bg-slate-200">
                                                    <div className="h-full rounded bg-blue-500" style={{ width: `${job.progressPercent}%` }} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                                            {formatRelativeDate(job.startedAt || job.createdAt)}
                                        </td>
                                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                                            {formatDuration(job.startedAt, job.completedAt)}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-1">
                                                {job.status === 'PENDING' && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); startJob(job.id); }}
                                                        className="p-1 text-gray-500 hover:text-green-700"
                                                        title="Start"
                                                    >
                                                        <PlayIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {(job.status === 'FAILED' || job.status === 'COMPLETED') && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); retryJob(job.id); }}
                                                        className="p-1 text-gray-500 hover:text-blue-700"
                                                        title="Retry"
                                                    >
                                                        <ArrowPathIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); cancelJob(job.id); }}
                                                        className="p-1 text-gray-500 hover:text-red-700"
                                                        title="Cancel"
                                                    >
                                                        <StopIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredJobs.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-gray-600 font-mono text-sm">
                                        {loading ? 'Loading...' : 'No runs found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="border-t border-slate-200 bg-white px-4 py-2 flex items-center justify-between">
                    <div className="font-mono text-xs text-slate-500">
                        Page {pagination.currentPage} of {pagination.totalPages || 1}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={pagination.prevPage}
                            disabled={!pagination.hasPrevPage}
                            className="px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <span className="px-3 py-1 rounded-lg font-mono text-xs text-slate-900 bg-slate-100 border border-slate-200">
                            {pagination.currentPage}
                        </span>
                        <button
                            onClick={pagination.nextPage}
                            disabled={!pagination.hasNextPage}
                            className="px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Right Panel - Job Details */}
            {selectedJob && (
                <JobDetailPanel
                    job={selectedJob}
                    onClose={() => {
                        setSelectedJob(null);
                        onSelectJob?.(null);
                    }}
                    onStart={() => startJob(selectedJob.id)}
                    onRetry={() => retryJob(selectedJob.id)}
                    onCancel={() => cancelJob(selectedJob.id)}
                    isStale={isJobStale(selectedJob, staleThreshold)}
                />
            )}
        </div>
    );
};

// Job Detail Panel Component
const JobDetailPanel = ({ job, onClose, onStart, onRetry, onCancel, isStale }) => {
    const [activeTab, setActiveTab] = useState('info');
    const workflow = getWorkflowByActivity(job.type);
    const label = jobTypeToLabel(job.type);
    const isRunning = job.status === 'RUNNING';

    const formatJson = (obj) => {
        if (!obj) return null;
        try {
            return JSON.stringify(typeof obj === 'string' ? JSON.parse(obj) : obj, null, 2);
        } catch {
            return obj.toString();
        }
    };

    return (
        <div className="w-96 border-l border-slate-200 bg-white flex flex-col">
            <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <span className="font-mono text-sm text-slate-900 font-bold">RUN DETAILS</span>
                <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {workflow && (
                            <div className={`${workflow.bgColor} p-1.5`}>
                                <workflow.icon className={`h-4 w-4 ${workflow.textColor}`} />
                            </div>
                        )}
                        <div>
                            <div className="font-mono text-sm text-slate-900 font-bold">{label || job.type}</div>
                            <div className="font-mono text-xs text-slate-500">{workflow?.label}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className={`px-2 py-0.5 font-mono text-xs font-bold ${JOB_STATUS_STYLES_BRUTALIST[job.status]}`}>
                            {job.status}
                        </span>
                        {isStale && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-mono text-xs font-bold animate-pulse">
                                STALE
                            </span>
                        )}
                    </div>
                </div>

                {/* Worker & Heartbeat for running jobs */}
                {isRunning && (job.workerId || job.heartbeatAt) && (
                    <div className={`rounded-lg border p-2 ${isStale ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                        {job.workerId && (
                            <div className="font-mono text-xs">
                                <span className="text-slate-500">Worker: </span>
                                <span className="text-slate-900">{job.workerId}</span>
                            </div>
                        )}
                        {job.heartbeatAt && (
                            <div className="font-mono text-xs mt-1">
                                <span className="text-slate-500">Last Heartbeat: </span>
                                <span className={isStale ? 'text-orange-700' : 'text-green-700'}>
                                    {formatRelativeDate(job.heartbeatAt)}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                    {job.status === 'PENDING' && (
                        <button onClick={onStart} className="flex-1 px-3 py-1.5 rounded-lg bg-green-600 text-on-accent font-mono text-xs font-bold hover:bg-green-500 transition-all duration-150">
                            START
                        </button>
                    )}
                    {(job.status === 'FAILED' || job.status === 'COMPLETED') && (
                        <button onClick={onRetry} className="flex-1 px-3 py-1.5 rounded-lg bg-blue-600 text-on-accent font-mono text-xs font-bold hover:bg-blue-500 transition-all duration-150">
                            RETRY
                        </button>
                    )}
                    {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                        <button onClick={onCancel} className="flex-1 px-3 py-1.5 rounded-lg bg-red-600 text-on-accent font-mono text-xs font-bold hover:bg-red-500 transition-all duration-150">
                            CANCEL
                        </button>
                    )}
                </div>

                {/* Tabs */}
                <div className="border-b border-slate-200 flex">
                    <button
                        onClick={() => setActiveTab('info')}
                        className={`px-3 py-2 font-mono text-xs transition-all duration-150 ${
                            activeTab === 'info' ? 'text-primary-dark border-b-2 border-primary' : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        INFO
                    </button>
                    <button
                        onClick={() => setActiveTab('timeline')}
                        className={`px-3 py-2 font-mono text-xs flex items-center gap-1 transition-all duration-150 ${
                            activeTab === 'timeline' ? 'text-primary-dark border-b-2 border-primary' : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        EVENTS
                        {isRunning && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />}
                    </button>
                </div>

                {activeTab === 'info' && (
                    <div className="space-y-4">
                        {/* Progress */}
                        {isRunning && job.progressPercent != null && (
                            <div className="surface-card p-3">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-mono text-xs text-slate-500 uppercase">Progress</span>
                                    <span className="font-mono text-xs font-bold text-slate-900">{job.progressPercent}%</span>
                                </div>
                                <div className="w-full rounded bg-slate-200 h-1.5">
                                    <div className="h-1.5 rounded bg-blue-500" style={{ width: `${job.progressPercent}%` }} />
                                </div>
                                {job.progressMessage && (
                                    <p className="mt-1 font-mono text-xs text-slate-500">{job.progressMessage}</p>
                                )}
                            </div>
                        )}

                        {/* IDs */}
                        <div className="space-y-2">
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Run ID</div>
                                <div className="font-mono text-xs text-slate-900 break-all">{job.id}</div>
                            </div>
                            {job.tenderId && (
                                <div>
                                    <div className="font-mono text-xs text-slate-400 uppercase">Tender ID</div>
                                    <a
                                        href={`https://www.bidlyzer.com/tender/${job.tenderId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs text-blue-600 hover:text-blue-700 break-all"
                                    >
                                        {job.tenderId}
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Timestamps */}
                        <div className="border-t border-slate-200 pt-4 grid grid-cols-2 gap-2">
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Created</div>
                                <div className="font-mono text-xs text-slate-900">{new Date(job.createdAt).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Started</div>
                                <div className="font-mono text-xs text-slate-900">{job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}</div>
                            </div>
                            <div>
                                <div className="font-mono text-xs text-slate-400 uppercase">Completed</div>
                                <div className="font-mono text-xs text-slate-900">{job.completedAt ? new Date(job.completedAt).toLocaleString() : '-'}</div>
                            </div>
                        </div>

                        {/* Schedule info */}
                        {job.isScheduled && (
                            <div className="border-t border-slate-200 pt-4">
                                <div className="font-mono text-xs text-slate-400 uppercase mb-2">Schedule</div>
                                <div className="surface-card p-2">
                                    <div className="flex justify-between font-mono text-xs">
                                        <span className="text-slate-500">Cron</span>
                                        <span className="text-slate-900">{job.cronSchedule}</span>
                                    </div>
                                    <div className="flex justify-between font-mono text-xs mt-1">
                                        <span className="text-slate-500">Status</span>
                                        <span className={job.enabled ? 'text-green-700' : 'text-yellow-700'}>
                                            {job.enabled ? 'ENABLED' : 'PAUSED'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Parameters */}
                        {job.parameters && (
                            <div className="border-t border-slate-200 pt-4">
                                <div className="font-mono text-xs text-slate-400 uppercase mb-2">Parameters</div>
                                <pre className="surface-card p-2 font-mono text-xs text-slate-700 overflow-x-auto max-h-48 overflow-y-auto">
                                    {formatJson(job.parameters)}
                                </pre>
                            </div>
                        )}

                        {/* Result */}
                        {job.result && (
                            <div className="border-t border-slate-200 pt-4">
                                <div className="font-mono text-xs text-green-600 uppercase mb-2">Result</div>
                                <pre className="surface-card p-2 font-mono text-xs text-slate-700 overflow-x-auto max-h-48 overflow-y-auto">
                                    {formatJson(job.result)}
                                </pre>
                            </div>
                        )}

                        {/* Error */}
                        {job.error && (
                            <div className="border-t border-slate-200 pt-4">
                                <div className="font-mono text-xs text-red-600 uppercase mb-2">Error</div>
                                <pre className="bg-red-50 border border-red-200 rounded-lg p-2 font-mono text-xs text-red-700 overflow-x-auto max-h-48 overflow-y-auto">
                                    {formatJson(job.error)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'timeline' && (
                    <JobEventTimeline jobId={job.id} autoRefresh={isRunning} />
                )}
            </div>
        </div>
    );
};

export default JobsTab;
